/**
 * useMSTGRenderer Hook
 * 
 * MediaStreamTrackGenerator (MSTG) integration for precise frame timestamps.
 * Provides encoder-grade 30 FPS video output with microsecond precision.
 * 
 * Features:
 * - MSTG for Chrome/Edge 94+ with exact timestamps
 * - Automatic fallback to captureStream for Firefox/Safari
 * - Frame duplication when render exceeds budget (optional)
 * - Proper resource cleanup (VideoFrame.close(), ImageBitmap.close())
 * 
 * Architecture:
 * - Worker sends tick → main thread renders → writeFrame submits to MSTG
 * - Timestamps are in MICROSECONDS (33,333 µs per frame at 30 FPS)
 * 
 * @see Base Docs/Canvas_Recording_Implementation_Plan.md - Task 2.1
 */

import { useRef, useCallback, useEffect } from 'react';
import { RECORDING_FLAGS } from '../constants/recordingFlags';
import { frameTimingCollector, safeCloseResource } from '../utils/PerformanceMonitor';

export interface MSTGFrameStats {
  frameNumber: number;
  timestamp: number;
  renderDurationMs: number;
  wasDuplicated: boolean;
}

export interface UseMSTGRendererReturn {
  hasMSTG: boolean;
  initMSTG: () => MediaStreamTrack | null;
  writeFrame: (canvas: HTMLCanvasElement, frameNumber: number, renderDurationMs: number) => Promise<MSTGFrameStats | null>;
  cleanup: () => void;
  getTrack: () => MediaStreamTrack | null;
  isTrackActive: () => boolean;
  getStats: () => { framesWritten: number; framesDuplicated: number };
}

// Frame timing constants
const FRAME_DURATION_US = 33_333; // 33.333ms in microseconds for 30 FPS

// Feature detection (run once at module load)
const hasMSTGSupport = typeof MediaStreamTrackGenerator !== 'undefined';
const hasVideoFrameSupport = typeof VideoFrame !== 'undefined';

export function useMSTGRenderer(): UseMSTGRendererReturn {
  // Refs for MSTG resources
  const generatorRef = useRef<MediaStreamTrackGenerator<VideoFrame> | null>(null);
  const writerRef = useRef<WritableStreamDefaultWriter<VideoFrame> | null>(null);
  const lastBitmapRef = useRef<ImageBitmap | null>(null);
  
  // Stopping flag - set synchronously BEFORE cleanup to prevent race conditions
  // This prevents new writeFrame calls from starting async work after stop is initiated
  const stoppingRef = useRef(false);
  
  // Stats tracking
  const framesWrittenRef = useRef(0);
  const framesDuplicatedRef = useRef(0);
  
  // CRITICAL FIX: Internal frame counter to prevent duplicate frame numbers
  // Multiple useVideoTicker instances may call writeFrame, each with their own counter
  // By using our own counter, we ensure sequential frame numbers
  const internalFrameCountRef = useRef(0);
  
  // Phase 3 - Backpressure detection
  const pendingWritesRef = useRef(0);
  const backpressureWarningShownRef = useRef(false);
  const BACKPRESSURE_THRESHOLD = 3; // Alert if >3 writes pending
  
  // First-frame bootstrap gate: tracks the in-flight bitmap creation promise
  // Concurrent frames await this promise to get access to the first bitmap
  const bitmapReadyPromiseRef = useRef<Promise<ImageBitmap> | null>(null);
  
  // Check if MSTG can be used (flag enabled + browser support)
  const canUseMSTG = RECORDING_FLAGS.MSTG_ENABLED && hasMSTGSupport && hasVideoFrameSupport;

  /**
   * Initialize MediaStreamTrackGenerator
   * Returns the video track or null if initialization fails
   */
  const initMSTG = useCallback((): MediaStreamTrack | null => {
    // If flag is disabled or browser doesn't support, return null immediately
    if (!canUseMSTG) {
      return null;
    }

    try {
      // Create MSTG with video kind
      const generator = new MediaStreamTrackGenerator({ kind: 'video' });
      
      // Set content hint for motion (better for recordings with movement)
      if ('contentHint' in generator) {
        (generator as any).contentHint = 'motion';
      }
      
      // Get writable stream writer
      const writer = generator.writable.getWriter();
      
      // Store references
      generatorRef.current = generator;
      writerRef.current = writer;
      
      // Reset stopping flag and stats
      stoppingRef.current = false;
      framesWrittenRef.current = 0;
      framesDuplicatedRef.current = 0;
      internalFrameCountRef.current = 0; // Reset internal frame counter
      
      // Phase 3: Reset backpressure tracking
      pendingWritesRef.current = 0;
      backpressureWarningShownRef.current = false;
      
      // Reset first-frame bootstrap promise
      bitmapReadyPromiseRef.current = null;
      
      console.log('[MSTG] Initialized - all counters reset');
      
      return generator;
    } catch (error) {
      console.warn('[useMSTGRenderer] MSTG initialization failed:', error);
      return null;
    }
  }, [canUseMSTG]);

  /**
   * Write a frame to the MSTG stream
   * Handles frame duplication if render exceeds budget
   */
  const writeFrame = useCallback(async (
    canvas: HTMLCanvasElement,
    _callerFrameNumber: number, // Ignored - we use internal counter to prevent duplicates
    renderDurationMs: number
  ): Promise<MSTGFrameStats | null> => {
    // Guard: check stopping flag FIRST (synchronous check prevents race conditions)
    if (stoppingRef.current) {
      return null;
    }
    
    const writer = writerRef.current;
    const track = generatorRef.current;
    
    // Guard: don't write if writer is missing or track is not live
    if (!writer || !track || track.readyState !== 'live') {
      return null;
    }
    
    // Guard: validate canvas dimensions to prevent GC warnings on empty frames
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      return null;
    }
    
    // CRITICAL: Assign frame number AFTER all guards pass
    // This prevents gaps in timestamps when frames are skipped
    const frameNumber = internalFrameCountRef.current;
    internalFrameCountRef.current++;
    
    // COMPREHENSIVE LOGGING: Track first 10 frames and every 100th frame
    const shouldLog = frameNumber < 10 || frameNumber % 100 === 0;
    
    // Calculate timestamp in microseconds (MSTG requires µs)
    const timestamp = frameNumber * FRAME_DURATION_US;
    const budgetMs = RECORDING_FLAGS.RENDER_BUDGET_MS;
    
    if (shouldLog) {
      console.log(`[MSTG] Frame ${frameNumber} START: timestamp=${timestamp}µs, pendingWrites=${pendingWritesRef.current}, hasBitmap=${!!lastBitmapRef.current}, hasBitmapPromise=${!!bitmapReadyPromiseRef.current}`);
    }
    
    let wasDuplicated = false;
    
    let frame: VideoFrame | null = null;
    
    // CRITICAL FIX: Serialize writes - if pending write exists, use cached bitmap
    // This prevents overlapping createImageBitmap calls that cause GC stalls
    // while maintaining frame cadence by duplicating the last good frame
    if (pendingWritesRef.current > 0 && RECORDING_FLAGS.FRAME_DUPLICATION) {
      if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} taking SERIALIZED path (pendingWrites=${pendingWritesRef.current})`);
      
      // First-frame bootstrap: if no bitmap yet but promise exists, await it
      let bitmapToUse = lastBitmapRef.current;
      if (!bitmapToUse && bitmapReadyPromiseRef.current) {
        if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} waiting for bitmap promise...`);
        try {
          // Wait for first frame's bitmap to become available
          bitmapToUse = await bitmapReadyPromiseRef.current;
          if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} got bitmap from promise: ${!!bitmapToUse}`);
        } catch {
          // First frame's bitmap creation failed - fall through to normal path
          bitmapToUse = null;
          if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} bitmap promise FAILED`);
        }
        
        // CRITICAL: Re-check stopping flag after await - cleanup may have started
        if (stoppingRef.current) {
          if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} SKIPPED after await: stopping flag set`);
          return null;
        }
      }
      
      if (bitmapToUse) {
        // Re-check writer is still valid after await (cleanup may have nullified it)
        const currentWriter = writerRef.current;
        const currentTrack = generatorRef.current;
        if (!currentWriter || !currentTrack || currentTrack.readyState !== 'live') {
          if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} SKIPPED: writer/track invalid after await`);
          return null;
        }
        
        // Use synchronous path with cached bitmap - no async createImageBitmap
        try {
          frame = new VideoFrame(bitmapToUse, {
            timestamp,
            duration: FRAME_DURATION_US,
          });
          
          await currentWriter.write(frame);
          safeCloseResource(frame, 'VideoFrame (serialized duplicate)');
          frame = null;
          
          framesWrittenRef.current++;
          framesDuplicatedRef.current++;
          
          if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} WRITTEN (serialized duplicate), total=${framesWrittenRef.current}`);
          
          // Record telemetry for duplicated frame
          if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
            const isHiddenTab = typeof document !== 'undefined' && document.visibilityState === 'hidden';
            frameTimingCollector.recordFrame({
              frameNumber,
              targetTimestamp: timestamp,
              actualTimestamp: timestamp,
              renderDurationMs: 0, // Instant - used cached bitmap
              isDuplicate: true,
              isHiddenTab,
              drift: 0,
            });
          }
          
          return {
            frameNumber,
            timestamp,
            renderDurationMs: 0,
            wasDuplicated: true,
          };
        } catch (error) {
          // Suppress "Stream closed" errors during shutdown - these are expected
          // Also check error message to catch cases where stoppingRef hasn't been set yet
          const isShutdownError = stoppingRef.current || 
            (error instanceof Error && error.message.includes('Stream closed'));
          if (frame) safeCloseResource(frame, 'VideoFrame (serialized error)');
          if (!isShutdownError) {
            console.error('[useMSTGRenderer] Failed to write serialized duplicate:', error);
          }
          return null;
        }
      }
      // No bitmap available yet (first frame still in flight with no promise) - skip frame
      if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} SKIPPED: no bitmap available (hasBitmap=${!!lastBitmapRef.current}, hasPromise=${!!bitmapReadyPromiseRef.current})`);
      return null;
    }
    
    // CRITICAL FIX: Start bitmap creation BEFORE incrementing pendingWrites
    // This ensures concurrent frames can await the bitmap promise instead of being skipped
    // Race condition: if pendingWrites++ happens before bitmapPromise is set,
    // concurrent calls see pendingWrites > 0 but bitmapReadyPromiseRef is null → frame skipped!
    if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} taking NORMAL path (fresh frame)`);
    
    let bitmapPromise: Promise<ImageBitmap> | null = null;
    if (RECORDING_FLAGS.FRAME_DUPLICATION) {
      bitmapPromise = createImageBitmap(canvas);
      bitmapReadyPromiseRef.current = bitmapPromise;
    }
    
    // Phase 3: Track pending writes for backpressure detection
    // NOW safe to increment - concurrent frames can await bitmapPromise above
    pendingWritesRef.current++;
    if (pendingWritesRef.current > BACKPRESSURE_THRESHOLD && !backpressureWarningShownRef.current) {
      console.warn(`[useMSTGRenderer] Backpressure detected: ${pendingWritesRef.current} pending writes. Encoder may be overwhelmed.`);
      backpressureWarningShownRef.current = true;
    }
    
    try {
      if (renderDurationMs <= budgetMs) {
        // Frame rendered on time - use current canvas
        frame = new VideoFrame(canvas, {
          timestamp,
          duration: FRAME_DURATION_US,
        });
        
        // Write frame to encoder (concurrent frames will await bitmapPromise above)
        await writer.write(frame);
        safeCloseResource(frame, 'VideoFrame'); // CRITICAL: prevent GPU memory exhaustion
        frame = null; // Mark as cleaned up
        
        // Finish bitmap creation and update ref
        if (bitmapPromise) {
          try {
            const newBitmap = await bitmapPromise;
            const oldBitmap = lastBitmapRef.current;
            lastBitmapRef.current = newBitmap;
            safeCloseResource(oldBitmap, 'previous bitmap');
          } finally {
            // Clear promise after completion (success or failure)
            bitmapReadyPromiseRef.current = null;
          }
        }
        
      } else if (RECORDING_FLAGS.FRAME_DUPLICATION && lastBitmapRef.current) {
        // Render exceeded budget - duplicate last good frame
        console.warn(`[useMSTGRenderer] Frame ${frameNumber} exceeded budget: ${renderDurationMs.toFixed(1)}ms > ${budgetMs}ms, duplicating`);
        
        frame = new VideoFrame(lastBitmapRef.current, {
          timestamp,
          duration: FRAME_DURATION_US,
        });
        
        await writer.write(frame);
        safeCloseResource(frame, 'VideoFrame (duplicated)');
        frame = null;
        
        wasDuplicated = true;
        framesDuplicatedRef.current++;
      } else {
        // Budget exceeded but no fallback available - write current frame anyway
        frame = new VideoFrame(canvas, {
          timestamp,
          duration: FRAME_DURATION_US,
        });
        
        await writer.write(frame);
        safeCloseResource(frame, 'VideoFrame (fallback)');
        frame = null;
      }
      
      framesWrittenRef.current++;
      pendingWritesRef.current--;
      
      if (shouldLog) console.log(`[MSTG] Frame ${frameNumber} WRITTEN (normal path), total=${framesWrittenRef.current}, duplicated=${wasDuplicated}`);
      
      // Record telemetry (Phase 3) - purely additive, gated by TELEMETRY_ENABLED
      if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
        const targetTimestamp = timestamp;
        const actualTimestamp = timestamp; // Using target as actual since MSTG provides precise timing
        const isHiddenTab = typeof document !== 'undefined' && document.visibilityState === 'hidden';
        const drift = 0; // MSTG timestamps are exact, no drift
        
        frameTimingCollector.recordFrame({
          frameNumber,
          targetTimestamp,
          actualTimestamp,
          renderDurationMs,
          isDuplicate: wasDuplicated,
          isHiddenTab,
          drift,
        });
      }
      
      return {
        frameNumber,
        timestamp,
        renderDurationMs,
        wasDuplicated,
      };
    } catch (error) {
      // Suppress "Stream closed" errors during shutdown - these are expected
      // Also check error message to catch cases where stoppingRef hasn't been set yet
      const isShutdownError = stoppingRef.current || 
        (error instanceof Error && error.message.includes('Stream closed'));
      // Ensure frame is cleaned up even on error (Memory Guard pattern)
      if (frame) {
        safeCloseResource(frame, 'VideoFrame (error cleanup)');
      }
      if (!isShutdownError) {
        console.error('[useMSTGRenderer] Failed to write VideoFrame:', error);
      }
      return null;
    } finally {
      // Phase 3: Decrement pending writes counter (always runs)
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
    }
  }, []);

  /**
   * Clean up all MSTG resources
   * CRITICAL: Set stopping flag FIRST to prevent race conditions with in-flight writeFrame calls
   * Uses safeCloseResource for robust cleanup even if close() throws
   */
  const cleanup = useCallback(() => {
    // Set stopping flag FIRST - this prevents any new writeFrame calls from proceeding
    stoppingRef.current = true;
    
    // Close writer using memory guard
    safeCloseResource(writerRef.current, 'MSTG writer');
    writerRef.current = null;
    
    // Close last bitmap using memory guard
    safeCloseResource(lastBitmapRef.current, 'last bitmap');
    lastBitmapRef.current = null;
    
    // Stop generator track (has stop() not close())
    if (generatorRef.current) {
      try {
        generatorRef.current.stop();
      } catch (error) {
        console.warn('[useMSTGRenderer] Failed to stop generator:', error);
      }
      generatorRef.current = null;
    }
    
    // Reset stats
    framesWrittenRef.current = 0;
    framesDuplicatedRef.current = 0;
    
    // Phase 3: Reset backpressure tracking
    pendingWritesRef.current = 0;
    backpressureWarningShownRef.current = false;
    
    // Reset first-frame bootstrap promise
    bitmapReadyPromiseRef.current = null;
  }, []);

  /**
   * Get the current MSTG track
   */
  const getTrack = useCallback((): MediaStreamTrack | null => {
    return generatorRef.current;
  }, []);
  
  /**
   * Check if MSTG track is active and ready for frame writing
   * More robust than just checking getTrack() - verifies track is live and not stopping
   */
  const isTrackActive = useCallback((): boolean => {
    // Check stopping flag first (set synchronously during cleanup)
    if (stoppingRef.current) return false;
    
    const track = generatorRef.current;
    const writer = writerRef.current;
    
    if (!track || !writer) return false;
    
    // Check track readyState is 'live' (not 'ended')
    if (track.readyState !== 'live') return false;
    
    return true;
  }, []);

  /**
   * Get frame statistics
   */
  const getStats = useCallback(() => ({
    framesWritten: framesWrittenRef.current,
    framesDuplicated: framesDuplicatedRef.current,
  }), []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    hasMSTG: canUseMSTG,
    initMSTG,
    writeFrame,
    cleanup,
    getTrack,
    isTrackActive,
    getStats,
  };
}

export default useMSTGRenderer;
